import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { contractGeneratorOperations, contractGeneratorSelectors } from 'Redux/ducks/contractGenerator';
import ErrorMessage from 'ViatoUi/Shared/Feedback/ErrorMessage/ErrorMessage';
import { FlexBox } from 'ViatoUi/Shared/Layouts/Flex';
import Spacer from 'ViatoUi/Shared/Layouts/Spacer';
import { Headline } from 'ViatoUi/Shared/Typo';
import InputNumber from 'ViatoUi/Web/FormElements/Inputs/InputNumber';

import AccommodationType from './AccommodationType';
import styles from './SettingsArea.module.scss';
import validate from './utilities/validate';

const SettingsArea = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation(['contractGenerator', 'translation']);
	const { isLoggedIn = false } = useSelector(contractGeneratorSelectors.getUser);

	const { TWELVE_MONTH_CONTRACT_TERM_PRICE_MULTIPLICATOR, ACCOMMODATION_TYPES } = useSelector(contractGeneratorSelectors.getConstants);
	const fieldInfo = useSelector(contractGeneratorSelectors.getFieldInfo);
	const { selectedRooms, selectedContractTermInMonths, selectedAccommodationType } = useSelector(
		contractGeneratorSelectors.getDataToSubmit
	);

	const [userHasAdjustedUnitAmount, setUserHasAdjustedUnitAmount] = useState(false);

	return (
		<FlexBox alignItems='center' className={styles.settingsArea} flexDirection='column'>
			<div id='viato-contract-generator-contract-settings' />
			<Headline type='h3' centered>
				{t('pages.step1.settingsArea.headline')}
			</Headline>
			<Spacer height='l' />
			<AccommodationType adjustUnitAmountOnChange={!userHasAdjustedUnitAmount} />
			<FlexBox className={styles.settings} justifyContent='center'>
				<FlexBox alignItems='center' className={styles.setting} flexDirection='column'>
					<strong>
						{t('pages.step1.settingsArea.amountRooms', {
							unit: t(`translation:${ACCOMMODATION_TYPES[selectedAccommodationType].UNIT_TRANSLATION_PATH}.plural`)
						})}
					</strong>
					<Spacer height='m' />
					<InputNumber
						className={styles.numberInput}
						value={selectedRooms}
						onChange={newValue => {
							setUserHasAdjustedUnitAmount(true);
							dispatch(contractGeneratorOperations.setDataToSubmit({ selectedRooms: newValue }));
							dispatch(
								contractGeneratorOperations.setFieldInfo({
									selectedRooms: validate(
										newValue,
										t,
										t(`translation:${ACCOMMODATION_TYPES[selectedAccommodationType].UNIT_TRANSLATION_PATH}.plural`)
									)
								})
							);
						}}
					/>
					{typeof fieldInfo.selectedRooms !== 'undefined' && !fieldInfo.selectedRooms.isValid && (
						<ErrorMessage errorMessages={fieldInfo.selectedRooms.errorMessages} />
					)}
				</FlexBox>

				<FlexBox alignItems='center' className={styles.setting} flexDirection='column'>
					<strong>{t('pages.step1.settingsArea.contractTerm')}</strong>
					<Spacer height='m' />
					<div className={styles.toggle}>
						<div
							className={styles.option}
							data-selected={selectedContractTermInMonths === 12}
							role='button'
							tabIndex={0}
							onClick={() => dispatch(contractGeneratorOperations.setDataToSubmit({ selectedContractTermInMonths: 12 }))}
						>
							12 {t('pages.step1.settingsArea.months')}
						</div>
						<div
							className={styles.option}
							data-selected={selectedContractTermInMonths === 24}
							role='button'
							tabIndex={0}
							onClick={() => dispatch(contractGeneratorOperations.setDataToSubmit({ selectedContractTermInMonths: 24 }))}
						>
							24 {t('pages.step1.settingsArea.months')}
						</div>
						{isLoggedIn && (
							<div
								className={styles.option}
								data-selected={selectedContractTermInMonths === 0}
								role='button'
								tabIndex={0}
								onClick={() => dispatch(contractGeneratorOperations.setDataToSubmit({ selectedContractTermInMonths: 0 }))}
							>
								{t('pages.step1.settingsArea.flex')}
							</div>
						)}
						<div className={styles.discount}>
							{t('pages.step1.settingsArea.discount', {
								amount: TWELVE_MONTH_CONTRACT_TERM_PRICE_MULTIPLICATOR * 100
							})}
						</div>
					</div>
				</FlexBox>
			</FlexBox>
		</FlexBox>
	);
};

export default SettingsArea;
